import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { Phone, Loader2, ShieldCheck, ArrowLeft, KeyRound, UserCheck } from "lucide-react";
import PMRLogo from "../components/PMRLogo";
import { ADMIN_MOCK } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const AgentLoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (localStorage.getItem("pmr_agent")) navigate("/agent", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendOtp = (e) => {
    e.preventDefault();
    const clean = phone.replace(/\D/g, "").slice(-10);
    if (clean.length !== 10) {
      toast({ title: "Invalid phone", description: "Please enter a 10-digit mobile number." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const agent = ADMIN_MOCK.agents.find((a) => a.phone === clean);
      if (!agent) {
        toast({ title: "Not a registered agent", description: "This number isn't in our agent database." });
        return;
      }
      setStep("otp");
      setResendIn(30);
      toast({ title: "OTP sent", description: `6-digit code sent to +91 ${clean}` });
    }, 700);
  };

  const verifyOtp = (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast({ title: "Enter 6-digit OTP", description: "Please complete the verification code." });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      const clean = phone.replace(/\D/g, "").slice(-10);
      const agent = ADMIN_MOCK.agents.find((a) => a.phone === clean);
      if (agent && otp === agent.otp) {
        localStorage.setItem("pmr_agent", JSON.stringify(agent));
        toast({ title: `Welcome, ${agent.name}`, description: "Signed in as agent." });
        navigate("/agent");
      } else {
        toast({ title: "Wrong OTP", description: "Check your code and try again." });
      }
    }, 700);
  };

  const resend = () => {
    setResendIn(30);
    setOtp("");
    toast({ title: "OTP re-sent", description: "A new code is on its way." });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 relative overflow-hidden flex items-center justify-center px-6 py-10">
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-lg">
            <PMRLogo size={30} color="#0f766e" accent="#f97316" />
          </div>
          <div className="text-white">
            <div className="font-bold text-lg tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>PMR Travels</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-400">Agent Portal</div>
          </div>
        </Link>

        {step === "phone" && (
          <form onSubmit={sendOtp} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <div>
              <div className="w-12 h-12 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center mb-3">
                <UserCheck className="w-5 h-5" />
              </div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Agent Sign in</h1>
              <p className="mt-1 text-sm text-slate-400">Enter your registered mobile number to receive an OTP.</p>
            </div>

            <div>
              <Label htmlFor="ph" className="text-slate-300">Mobile Number</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">+91</span>
                <Input
                  id="ph"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9397363140"
                  maxLength={10}
                  className="pl-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-orange-400"
                />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending OTP...</> : "Send OTP"}
            </Button>

            <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Demo agents — <span className="font-mono text-slate-300">9397363140 or 9876543210</span>
            </div>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp} className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 space-y-5">
            <button type="button" onClick={() => { setStep("phone"); setOtp(""); }} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white">
              <ArrowLeft className="w-3 h-3" /> Change number
            </button>
            <div>
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 text-orange-400 flex items-center justify-center mb-3">
                <KeyRound className="w-5 h-5" />
              </div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>Enter OTP</h1>
              <p className="mt-1 text-sm text-slate-400">6-digit code sent to <span className="text-white font-semibold">+91 {phone.slice(-10)}</span></p>
            </div>

            <div className="flex justify-center py-2">
              <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="bg-white/5 text-white border-white/20 w-11 h-12 text-lg" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button type="submit" disabled={loading || otp.length !== 6} className="w-full h-11 bg-orange-500 hover:bg-orange-600">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : "Verify & Sign in"}
            </Button>

            <div className="text-center text-xs text-slate-400">
              {resendIn > 0 ? (
                <>Resend code in <span className="text-white font-semibold">{resendIn}s</span></>
              ) : (
                <button type="button" onClick={resend} className="text-orange-400 hover:text-orange-300 font-semibold">Resend OTP</button>
              )}
            </div>

            <div className="pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Demo OTP — <span className="font-mono text-slate-300">142536</span>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-xs text-slate-500 space-x-3">
          <Link to="/" className="hover:text-white">← Back to website</Link>
          <span>·</span>
          <Link to="/admin/login" className="hover:text-white">Admin login</Link>
        </div>
      </div>
    </div>
  );
};

export default AgentLoginPage;
